import { useQueryClient } from '@tanstack/react-query';
import type { Reply } from 'models';
import { useCallback } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { getContentObjectRepliesQueryKey } from 'features/feed/queries/keys';
import type { UseCommentLikeActionType } from '../queries';

export type UpdateCommentLikeProperties = {
  action: 'like' | 'unlike';
};

const updateLikeCount = ({
  action,
  reply,
}: {
  action: UpdateCommentLikeProperties['action'];
  reply: Reply;
}): Reply => ({
  ...reply,
  liked: action === 'like',
  likesCount: reply.likesCount + (action === 'like' ? 1 : -1),
});

export const useUpdateCommentLike = ({ action }: UpdateCommentLikeProperties) => {
  const queryClient = useQueryClient();

  return useCallback(
    ({ contentObjectId, id }: UseCommentLikeActionType) => {
      queryClient.setQueriesData<Reply[]>(
        { queryKey: getContentObjectRepliesQueryKey({ id: contentObjectId }) },
        (replies) =>
          replies?.map((reply) => {
            if (reply.id === id) {
              return updateLikeCount({ action, reply });
            }

            if (reply.replies) {
              const updatedChildren = reply.replies.map((child) =>
                child.id === id ? updateLikeCount({ action, reply: child }) : child,
              );

              return {
                ...reply,
                replies: updatedChildren,
              };
            }

            return reply;
          }),
      );
    },
    [action, queryClient],
  );
};
